import React, {
  InputHTMLAttributes,
  MouseEvent,
  TextareaHTMLAttributes,
  useCallback,
  useState,
} from 'react';
import { ErrorMessage, Field } from 'formik';

import {
  FieldErrorMessage,
  FormField,
  InputContainer,
  InputFieldContainer,
  ShowPasswordButton,
  TextArea,
} from './components';
import eyeIcon from 'assets/icons/Eye.png';

type InputFieldProps = InputHTMLAttributes<HTMLInputElement> &
  TextareaHTMLAttributes<HTMLTextAreaElement> & {
    type?: string;
    textarea?: boolean;
    className?: string;
    label?: string;
    'aria-label'?: string;
    'aria-describedby'?: string;
    error?: string;
  };

const InputField = ({
  type = 'text',
  textarea = false,
  className,
  label,
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedBy,
  error,
  ...props
}: InputFieldProps) => {
  const [showPassword, setShowPassword] = useState(false);

  const toggleShowPassword = useCallback((evt: MouseEvent) => {
    evt.preventDefault(); //This is to avoid the input field to be marked as dirty when clicking the trailing button
    setShowPassword((showPassword) => !showPassword);
  }, []);

  const fieldId = props.id || props.name;
  const errorId = `${fieldId}-error`;
  const computedAriaDescribedBy = error
    ? `${ariaDescribedBy || ''} ${errorId}`.trim()
    : ariaDescribedBy;

  return (
    <InputFieldContainer>
      {label && (
        <label htmlFor={fieldId} className="visually-hidden">
          {label}
        </label>
      )}
      {textarea ? (
        <Field
          className={className}
          {...props}
          as={TextArea}
          id={fieldId}
          aria-label={ariaLabel || label}
          aria-describedby={computedAriaDescribedBy}
          aria-invalid={!!error}
        />
      ) : (
        <InputContainer className={className}>
          <FormField
            {...props}
            type={showPassword ? 'text' : type}
            id={fieldId}
            aria-label={ariaLabel || label}
            aria-describedby={computedAriaDescribedBy}
            aria-invalid={!!error}
          />
          {type === 'password' && (
            <ShowPasswordButton
              type="button"
              onClick={toggleShowPassword}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              aria-pressed={showPassword}
              tabIndex={0}>
              <img src={eyeIcon} alt="" aria-hidden="true" />
            </ShowPasswordButton>
          )}
        </InputContainer>
      )}
      <ErrorMessage
        name={props.name ?? ''}
        component={FieldErrorMessage}
        id={errorId}
        // @ts-expect-error Property 'role' does not exist on ErrorMessageProps, but needed for accessibility
        role="alert"
        aria-live="polite"
      />
    </InputFieldContainer>
  );
};

export default InputField;
